import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Filter, X, Clock, User, Loader2, ChevronLeft, ChevronRight, FolderOpen, AlertTriangle, CheckCircle2, Bell, MoreVertical, TrendingUp, LayoutGrid, List, Flame, CheckSquare } from 'lucide-react'
import { Card, Badge, StatusIndicator } from '../atoms'
import { cn } from '../atoms'
import { SearchBar } from '../molecules/SearchBar'
import { useCases, useCaseReferences, useCaseStats, useUpdateCaseStatus } from '../../hooks/useCases'
import { CasesKanbanBoard } from '../organisms/CasesKanbanBoard'
import { useAuth } from '../../store/auth.store'
import { useAreas } from '../../hooks/useAreas'
import { useDebounce } from '../../hooks/useDebounce'
import { IndicatorCard } from '../molecules/IndicatorCard'

const criticidadBadge: Record<string, { className: string, label: string }> = {
  CM: { className: 'bg-red-600 text-white', label: 'Crítico Máximo' },
  CRITICO_MAXIMO: { className: 'bg-red-600 text-white', label: 'Crítico Máximo' },
  C: { className: 'bg-red-100 text-red-700 border border-red-300', label: 'Crítico' },
  CRITICO: { className: 'bg-red-100 text-red-700 border border-red-300', label: 'Crítico' },
  critical: { className: 'bg-red-100 text-red-700 border border-red-300', label: 'Crítico' },
  A: { className: 'bg-amber-100 text-amber-700 border border-amber-300', label: 'Alto' },
  ALTO: { className: 'bg-amber-100 text-amber-700 border border-amber-300', label: 'Alto' },
  high: { className: 'bg-amber-100 text-amber-700 border border-amber-300', label: 'Alto' },
  M: { className: 'bg-blue-100 text-blue-700 border border-blue-300', label: 'Medio' },
  MEDIO: { className: 'bg-blue-100 text-blue-700 border border-blue-300', label: 'Medio' },
  medium: { className: 'bg-blue-100 text-blue-700 border border-blue-300', label: 'Medio' },
  B: { className: 'bg-green-100 text-green-700 border border-green-300', label: 'Bajo' },
  BAJO: { className: 'bg-green-100 text-green-700 border border-green-300', label: 'Bajo' },
  low: { className: 'bg-green-100 text-green-700 border border-green-300', label: 'Bajo' },
  INF: { className: 'bg-gray-100 text-gray-600 border border-gray-300', label: 'Informativo' },
  INFORMATIVO: { className: 'bg-gray-100 text-gray-600 border border-gray-300', label: 'Informativo' },
}

function getCriticalityBadge(caseItem: any) {
  const rawKey = caseItem.criticidad || caseItem.emails?.[0]?.criticidad || caseItem.riskLevel?.name || ''
  const key = String(rawKey).trim()
  const badge = criticidadBadge[key] || criticidadBadge[key.toUpperCase()]
  if (badge) {
    return (
      <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', badge.className)}>
        {badge.label}
      </span>
    )
  }
  if (caseItem.riskLevel) {
    return (
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: `${caseItem.riskLevel.color}20`, color: caseItem.riskLevel.color, border: `1px solid ${caseItem.riskLevel.color}40` }}
      >
        {caseItem.riskLevel.label || caseItem.riskLevel.name}
      </span>
    )
  }
  return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300">—</span>
}

function slaStatus(date: string | undefined): { color: string; label: string } | null {
  if (!date) return null
  const now = new Date().getTime()
  const d = new Date(date).getTime()
  if (d < now) return { color: 'text-red-600', label: 'Vencido' }
  if (d < now + 4 * 60 * 60 * 1000) return { color: 'text-amber-600', label: 'Por vencer' }
  return { color: 'text-green-600', label: 'En plazo' }
}

export function CasesPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list')
  const updateStatusMutation = useUpdateCaseStatus()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('statusId') || '')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [assignedFilter, setAssignedFilter] = useState('')
  const [areaFilter, setAreaFilter] = useState('')
  const [slaFilter, setSlaFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const debouncedSearch = useDebounce(search, 300)
  const { data: references } = useCaseReferences()

  // Handle URL search parameters on load / change
  const statusParam = searchParams.get('status')
  const statusIdParam = searchParams.get('statusId')
  const priorityParam = searchParams.get('priority')
  const priorityIdParam = searchParams.get('priorityId')
  const criticalityParam = searchParams.get('criticality')
  const slaParam = searchParams.get('sla') || searchParams.get('slaStatus')
  const assigneeParam = searchParams.get('assignee') || searchParams.get('assignedTo')

  useEffect(() => {
    // Status
    if (statusIdParam) {
      setStatusFilter(statusIdParam)
    } else if (statusParam && references?.statuses) {
      const match = references.statuses.find(
        (s: any) =>
          s.name?.toLowerCase() === statusParam.toLowerCase() ||
          s.label?.toLowerCase() === statusParam.toLowerCase() ||
          (statusParam.toLowerCase() === 'open' && (s.name === 'open' || s.name === 'new' || s.name === 'in_progress')) ||
          (statusParam.toLowerCase() === 'in_progress' && (s.name === 'in_progress' || s.name === 'in-progress')) ||
          (statusParam.toLowerCase() === 'closed' && (s.name === 'closed' || s.name === 'resolved'))
      )
      if (match) {
        setStatusFilter(match.id)
      }
    }

    // Priority / Criticality
    if (priorityIdParam) {
      setPriorityFilter(priorityIdParam)
    } else if ((priorityParam || criticalityParam) && references?.priorities) {
      const pStr = (priorityParam || criticalityParam || '').toLowerCase()
      if (pStr.includes('crit') || pStr.includes('alt') || pStr.includes('high')) {
        const highIds = references.priorities
          .filter((p: any) => isHighPriority(p.name) || isHighPriority(p.label))
          .map((p: any) => p.id)
          .join(',')
        if (highIds) setPriorityFilter(highIds)
      } else {
        const match = references.priorities.find((p: any) => p.name?.toLowerCase() === pStr || p.label?.toLowerCase() === pStr)
        if (match) setPriorityFilter(match.id)
      }
    }

    // SLA
    if (slaParam) {
      const s = slaParam.toLowerCase()
      if (s === 'atrisk' || s === 'at_risk' || s === 'risk') setSlaFilter('at_risk')
      else if (s === 'breached' || s === 'vencido' || s === 'vencidos') setSlaFilter('breached')
      else if (s === 'ontrack' || s === 'on_track') setSlaFilter('on_track')
    }

    // Assignee
    if (assigneeParam) {
      if (assigneeParam === 'me' && user?.id) {
        setAssignedFilter(user.id)
      } else if (assigneeParam !== 'me') {
        setAssignedFilter(assigneeParam)
      }
    }
  }, [statusParam, statusIdParam, priorityParam, priorityIdParam, criticalityParam, slaParam, assigneeParam, references?.statuses, references?.priorities, user?.id])

  const { data, isLoading } = useCases({
    page,
    limit,
    search: debouncedSearch || undefined,
    statusId: statusFilter || undefined,
    priorityId: priorityFilter || undefined,
    assignedTo: assignedFilter || undefined,
    areaId: areaFilter || undefined,
    slaStatus: slaFilter || undefined,
  })
  const { data: stats } = useCaseStats()
  const { data: areas } = useAreas()

  const hasActiveFilters = statusFilter || priorityFilter || assignedFilter || areaFilter || slaFilter
  const pagination = data?.pagination
  const totalPages = pagination?.totalPages || 1
  const currentPage = pagination?.page || 1

  const clearFilters = () => {
    setStatusFilter('')
    setPriorityFilter('')
    setAssignedFilter('')
    setAreaFilter('')
    setSlaFilter('')
    setPage(1)
  }

  const statusId = (name: string) => references?.statuses?.find((s: any) => s.name === name || s.label?.toLowerCase() === name.toLowerCase())?.id || ''
  const statusCount = (name: string) => {
    const statusObj = references?.statuses?.find((s: any) => s.name === name || s.label?.toLowerCase() === name.toLowerCase())
    if (!statusObj) return 0
    return (stats as any)?.byStatus?.[statusObj.label] || (stats as any)?.byStatus?.[statusObj.name] || 0
  }
  const applyStatus = (name: string) => {
    const id = statusId(name)
    setStatusFilter(prev => prev === id ? '' : id)
    setPriorityFilter('')
    setSlaFilter('')
    setPage(1)
  }

  const priorityList = references?.priorities || []
  const isHighPriority = (str?: string) => {
    if (!str) return false
    const norm = str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    return norm.includes('crit') || norm.includes('alt') || norm.includes('high') || norm.includes('urg')
  }
  const highPriorityObjs = priorityList.filter((p: any) => isHighPriority(p.name) || isHighPriority(p.label))
  const highPriorityIds = highPriorityObjs.map((p: any) => p.id).join(',')
  const highPriorityCount = useMemo(() => {
    const byPriority = (stats as any)?.byPriority
    if (!byPriority) return 0
    return Object.entries(byPriority)
      .filter(([k]) => isHighPriority(k))
      .reduce((sum, [, v]) => sum + Number(v || 0), 0)
  }, [stats])

  const goToPage = (p: number) => {
    if (p >= 1 && p <= totalPages) setPage(p)
  }

  const getPageNumbers = (): number[] => {
    const pages: number[] = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1)
    }
    for (let i = start; i <= end; i++) pages.push(i)
    return pages
  }

  return (
    <div className="space-y-5 max-w-[1700px] mx-auto">
      <header>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-950 dark:text-gray-100">Casos</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">Gestión operativa y seguimiento de casos</p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" aria-label="Resumen de casos">
        <IndicatorCard
          label="Casos activos"
          value={(stats as any)?.total || pagination?.total || 0}
          icon={FolderOpen}
          tone="blue"
          active={!statusFilter && !priorityFilter && !assignedFilter && !areaFilter && !slaFilter}
          onClick={clearFilters}
        />
        <IndicatorCard
          label="Casos prioritarios"
          value={highPriorityCount}
          icon={AlertTriangle}
          tone="red"
          active={Boolean(priorityFilter && priorityFilter === highPriorityIds)}
          onClick={() => {
            setPriorityFilter(prev => prev === highPriorityIds ? '' : highPriorityIds)
            setStatusFilter('')
            setSlaFilter('')
            setPage(1)
          }}
        />
        <IndicatorCard
          label="En proceso"
          value={statusCount('in_progress')}
          icon={TrendingUp}
          tone="amber"
          active={Boolean(statusFilter && statusFilter === statusId('in_progress'))}
          onClick={() => applyStatus('in_progress')}
        />
        <IndicatorCard
          label="Cerrados"
          value={statusCount('closed')}
          icon={CheckCircle2}
          tone="green"
          active={Boolean(statusFilter && statusFilter === statusId('closed'))}
          onClick={() => applyStatus('closed')}
        />
        <IndicatorCard
          label="SLA en riesgo"
          value={((stats as any)?.sla?.atRisk || 0) + ((stats as any)?.sla?.breached || 0)}
          icon={Clock}
          tone="purple"
          active={slaFilter === 'at_risk'}
          onClick={() => {
            setSlaFilter(prev => prev === 'at_risk' ? '' : 'at_risk')
            setStatusFilter('')
            setPriorityFilter('')
            setPage(1)
          }}
        />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:bg-gray-900 dark:border-gray-700">
        <div className="flex flex-col xl:flex-row gap-3">
          <div className="flex-1 min-w-[220px]"><SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1) }} mode="simple" placeholder="Buscar casos..." /></div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-300 dark:border-gray-700 p-0.5 bg-slate-100 dark:bg-gray-800">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all',
                  viewMode === 'list'
                    ? 'bg-white dark:bg-gray-900 text-slate-900 dark:text-slate-100 shadow-2xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                )}
              >
                <List className="w-3.5 h-3.5" />
                Lista
              </button>
              <button
                type="button"
                onClick={() => setViewMode('kanban')}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all',
                  viewMode === 'kanban'
                    ? 'bg-white dark:bg-gray-900 text-slate-900 dark:text-slate-100 shadow-2xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                )}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                Tablero Kanban
              </button>
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={cn('inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg transition-colors', showFilters || hasActiveFilters ? 'bg-primary-50 text-primary-700 border-primary-300' : 'text-slate-700 bg-white border-slate-300 hover:bg-slate-50')}><Filter className="w-4 h-4" /> Más filtros</button>
            <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1) }} className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-900" title="Casos por página">{[10, 20, 50, 100].map(l => <option key={l} value={l}>{l} / pág</option>)}</select>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-gray-800 text-xs">
            <span className="text-slate-500">Filtros activos ({pagination?.total || 0} casos):</span>
            {statusFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                Estado: {references?.statuses?.find((s: any) => s.id === statusFilter)?.label || statusFilter}
                <button onClick={() => setStatusFilter('')} className="hover:text-blue-900"><X className="w-3 h-3" /></button>
              </span>
            )}
            {priorityFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
                Prioridad: {references?.priorities?.find((p: any) => p.id === priorityFilter)?.label || 'Alta/Crítica'}
                <button onClick={() => setPriorityFilter('')} className="hover:text-red-900"><X className="w-3 h-3" /></button>
              </span>
            )}
            {slaFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                SLA: {slaFilter === 'at_risk' ? 'Próximos a vencer / En riesgo' : slaFilter === 'breached' ? 'Vencidos' : 'En tiempo'}
                <button onClick={() => setSlaFilter('')} className="hover:text-amber-900"><X className="w-3 h-3" /></button>
              </span>
            )}
            {assignedFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                Asignado: {assignedFilter === user?.id ? 'A mí' : (references?.users?.find((u: any) => u.id === assignedFilter)?.firstName || assignedFilter)}
                <button onClick={() => setAssignedFilter('')} className="hover:text-purple-900"><X className="w-3 h-3" /></button>
              </span>
            )}
            {areaFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                Área: {areas?.find((a: any) => a.id === areaFilter)?.name || areaFilter}
                <button onClick={() => setAreaFilter('')} className="hover:text-emerald-900"><X className="w-3 h-3" /></button>
              </span>
            )}
            <button onClick={clearFilters} className="text-xs text-primary-600 hover:text-primary-700 font-semibold ml-2">
              Limpiar todo
            </button>
          </div>
        )}
        {showFilters && <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-gray-700">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todos los estados</option>
                {references?.statuses
                  ?.filter((s: any) => ['new', 'in_progress', 'closed'].includes(s.name))
                  .map((s: any) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Prioridad</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="block w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900 focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todas las criticidades</option>
                {references?.priorities?.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Asignado a</label>
              <select value={assignedFilter} onChange={(e) => { setAssignedFilter(e.target.value); setPage(1) }} className="block w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900">
                <option value="">Todos</option>
                {references?.users?.map((u: any) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Área</label>
              <select value={areaFilter} onChange={(e) => { setAreaFilter(e.target.value); setPage(1) }} className="block w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900">
                <option value="">Todas</option>
                {areas?.map((area: any) => <option key={area.id} value={area.id}>{area.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">SLA</label>
              <select value={slaFilter} onChange={(e) => { setSlaFilter(e.target.value); setPage(1) }} className="block w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white text-slate-900">
                <option value="">Todos</option><option value="at_risk">En riesgo</option><option value="breached">Vencidos</option>
              </select>
            </div>
            <div className="flex items-end">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
        }
      </section>

      {viewMode === 'kanban' ? (
        <CasesKanbanBoard
          cases={data?.data || []}
          onStatusChange={(caseId, statusName) => {
            updateStatusMutation.mutate({ id: caseId, statusName })
          }}
          onCaseClick={(caseId) => navigate(`/cases/${caseId}`)}
          isLoading={isLoading}
        />
      ) : (
        <Card className="overflow-hidden rounded-xl border-slate-200 shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : !data?.data?.length ? (
          <p className="text-sm text-gray-500 text-center py-12">No hay casos</p>
        ) : (
          <>
          <div className="overflow-visible">
            <table className="data-table w-full table-fixed">
              <thead>
                <tr>
                  <th className="w-[30%]">Caso</th>
                  <th className="w-[14%]">Criticidad</th>
                  <th className="w-[13%]">Estado</th>
                  <th className="w-[23%]">Asignado</th>
                  <th className="w-[14%]">Vencimiento</th>
                  <th className="w-[6%] text-right pr-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((caseItem: any) => (
                  <tr key={caseItem.id} onClick={() => navigate(`/cases/${caseItem.id}`)} className="cursor-pointer">
                    <td>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{caseItem.subject || '(Sin asunto)'}</p>
                          {(caseItem._count?.communications >= 3 || caseItem.communications?.length >= 3) && (
                            <span className="inline-flex items-center gap-0.5 text-2xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-semibold" title="Hilo Activo / Reincidente con múltiples mensajes">
                              <Flame className="w-3 h-3 text-amber-600 animate-pulse" />
                              Hilo Activo
                            </span>
                          )}
                          {Array.isArray(caseItem.tasks) && caseItem.tasks.length > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-2xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 font-medium">
                              <CheckSquare className="w-3 h-3 text-slate-500" />
                              {caseItem.tasks.filter((t: any) => t.isCompleted).length}/{caseItem.tasks.length}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 font-mono">{caseItem.caseNumber}</p>
                      </div>
                    </td>
                    <td>
                      {getCriticalityBadge(caseItem)}
                    </td>
                    <td>
                      <StatusIndicator
                        status={(caseItem.status?.name || caseItem.status) as any}
                        label={caseItem.status?.label || caseItem.status?.name}
                      />
                    </td>
                    <td>
                      {(() => {
                        const allAssignees = [
                          caseItem.assignee ? { ...caseItem.assignee, isPrimary: true } : (caseItem.assignedTo ? { firstName: caseItem.assignedTo, lastName: '', isPrimary: true } : null),
                          ...(caseItem.coAssignees || []).map((u: any) => ({ ...u, isPrimary: false }))
                        ].filter(Boolean)

                        if (allAssignees.length === 0) {
                          return (
                            <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500 text-xs">
                              <User className="w-3.5 h-3.5 flex-shrink-0" />
                              <span>Sin asignar</span>
                            </div>
                          )
                        }

                        const primary = allAssignees.find((u: any) => u.isPrimary) || allAssignees[0]
                        const primaryName = `${primary.firstName || ''} ${primary.lastName || ''}`.trim() || primary.email || 'Asignado'
                        const extraCount = allAssignees.length - 1

                        if (extraCount === 0) {
                          return (
                            <span
                              className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border border-blue-200 dark:border-blue-800 shadow-sm"
                              title={`Responsable: ${primaryName}`}
                            >
                              <User className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate max-w-[150px]">{primaryName}</span>
                            </span>
                          )
                        }

                        const tooltipList = allAssignees
                          .map((u: any) => `${u.isPrimary ? '👑 ' : '• '}${(`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Usuario')}`)
                          .join('\n')

                        return (
                          <div className="relative inline-flex items-center gap-1.5 group/assignee py-1" title={tooltipList}>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border border-blue-200 dark:border-blue-800 shadow-sm">
                              <User className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate max-w-[110px]">{primaryName}</span>
                            </span>

                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700 shadow-sm cursor-help hover:bg-gray-200 transition-colors">
                              +{extraCount} asignado{extraCount > 1 ? 's' : ''}
                            </span>

                            {/* Floating Popover on Hover */}
                            <div className="invisible opacity-0 group-hover/assignee:visible group-hover/assignee:opacity-100 transition-all duration-150 absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-2.5 min-w-[210px] max-w-[280px] pointer-events-none">
                              <p className="text-[10px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-wider mb-1.5 pb-1 border-b border-gray-100 dark:border-gray-800">
                                Asignados ({allAssignees.length})
                              </p>
                              <div className="space-y-1">
                                {allAssignees.map((u: any, idx: number) => {
                                  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Usuario'
                                  return (
                                    <div key={u.id || idx} className="flex items-center justify-between gap-2 text-xs py-0.5">
                                      <div className="flex items-center gap-1.5 min-w-0">
                                        <User className={cn("w-3.5 h-3.5 flex-shrink-0", u.isPrimary ? "text-blue-600 dark:text-blue-400" : "text-gray-400")} />
                                        <span className={cn("truncate", u.isPrimary ? "font-semibold text-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-300")}>
                                          {name}
                                        </span>
                                      </div>
                                      {u.isPrimary ? (
                                        <span className="text-[9px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 px-1.5 py-0.2 rounded flex-shrink-0">
                                          Principal
                                        </span>
                                      ) : (
                                        <span className="text-[9px] text-gray-400 flex-shrink-0">
                                          Co-asignado
                                        </span>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          </div>
                        )
                      })()}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm whitespace-nowrap">
                        <Clock className={cn('w-3.5 h-3.5', slaStatus(caseItem.slaResponseDue)?.color || 'text-gray-400')} />
                        <span className={cn(slaStatus(caseItem.slaResponseDue)?.color || 'text-gray-500')}>
                          {caseItem.slaResponseDue
                            ? new Date(caseItem.slaResponseDue).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                            : '-'}
                        </span>
                        {caseItem.slaResponseDue && (
                          <span className={cn('text-[10px]', slaStatus(caseItem.slaResponseDue)?.color || 'text-gray-400')}>
                            ({slaStatus(caseItem.slaResponseDue)?.label})
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <MoreVertical className="w-4 h-4 text-gray-400 ml-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Mostrando <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> –{' '}
                <span className="font-medium">{Math.min(currentPage * limit, pagination?.total || 0)}</span> de{' '}
                <span className="font-medium">{pagination?.total || 0}</span> casos
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="inline-flex items-center p-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Anterior"
                  aria-label="Página anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {getPageNumbers().map((p) => (
                  <button
                    key={p}
                    onClick={() => goToPage(p)}
                    className={cn(
                      'inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-md text-sm font-medium transition-colors',
                      p === currentPage
                        ? 'bg-primary-600 text-white border border-primary-600'
                        : 'border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                    )}
                    aria-label={`Ir a página ${p}`}
                    aria-current={p === currentPage ? 'page' : undefined}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="inline-flex items-center p-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Siguiente"
                  aria-label="Página siguiente"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          </>
        )}
        </Card>
      )}
    </div>
  )
}
